import React, { useMemo } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Container } from "@/components/ui-kit";
import legalDocuments from "@/data/legalDocuments.generated.json";
import { TAGALOG_LEGAL_GUIDES } from "@/data/tagalogLegalGuides";
import { isTagalogSelected } from "@/data/tagalogTranslations";
import { useRegion } from "@/hooks/useRegion";

const NAMED_HEADINGS = new Set([
  "Short Version", "In-Depth Version", "In Plain English", "Payments", "Previews", "Refunds",
  "Additional Work", "Ownership and Access", "If Payment Becomes Overdue", "If Services Are Suspended",
  "Standard Overdue Timeline", "Website Setup", "Hosting", "Maintenance and Updates",
  "Monitoring and Issue Handling", "Security and Protection", "Technical Fixes and Responsibility",
  "Content Edits", "Communication and Expectations", "What We Do Not Provide", "Contact",
  "No Marketing or Business-Result Guarantees", "No Advertising or Campaign Management by Default",
  "No Unlimited Website Changes", "No Content Creation by Default", "No Legal, Tax, or Regulatory Advice",
  "No Absolute Uptime or Security Guarantee", "Responsibility Is Assigned Clearly",
]);

const numberedHeading = /^(\d+(?:\.\d+)*)(?:\.)?\s+[A-Z]/;
const isHeading = (text) => numberedHeading.test(text) || NAMED_HEADINGS.has(text);
const headingLevel = (text) => {
  const match = text.match(numberedHeading);
  return match?.[1]?.includes(".") ? 3 : 2;
};
const slugify = (text, index) => `${text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "section"}-${index + 1}`;

function InlineText({ text }) {
  const parts = text.split(/(info@mysitecare\.eu|(?:https?:\/\/)?mysitecare\.eu(?:\/ph)?)/gi);
  return parts.map((part, index) => {
    if (/^info@mysitecare\.eu$/i.test(part)) {
      return <a key={index} href="mailto:info@mysitecare.eu" className="text-[#059669] hover:underline">{part}</a>;
    }
    if (/^(?:https?:\/\/)?mysitecare\.eu(?:\/ph)?$/i.test(part)) {
      const href = part.startsWith("http") ? part : `https://${part}`;
      return <a key={index} href={href} className="text-[#059669] hover:underline">{part}</a>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

export default function LegalDocumentPage({ documentKey }) {
  const { region, isPH } = useRegion();
  const document = legalDocuments[region][documentKey];
  const tagalogGuide = isPH && isTagalogSelected() ? TAGALOG_LEGAL_GUIDES[documentKey] : null;
  const sections = useMemo(
    () => document.blocks.map((text, index) => ({ text, index, heading: isHeading(text), level: headingLevel(text), id: slugify(text, index) }))
      .filter((item) => item.heading && item.level === 2),
    [document]
  );

  return (
    <div data-testid={`legal-document-${documentKey}`}>
      <section className="bg-[#0F172A]">
        <Container className="py-14 md:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399]">{tagalogGuide ? "Pilipinas" : isPH ? "Philippines" : "EU / International"}</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold font-display text-white text-balance">{tagalogGuide?.title || document.title}</h1>
          {tagalogGuide?.subtitle && <p className="mt-3 text-slate-300 text-lg">{tagalogGuide.subtitle}</p>}
          <p className="mt-4 text-sm text-slate-400">{tagalogGuide ? "Huling na-update" : "Last updated"}: {tagalogGuide ? "1 Setyembre 2026" : document.updated}</p>
        </Container>
      </section>

      <section className="py-12 md:py-16 bg-[#F8FAFC]">
        <Container className="max-w-6xl">
          {tagalogGuide && (
            <div className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="tagalog-legal-guide-notice">
              <p className="font-semibold text-emerald-950">Sinuring gabay sa Filipino—hindi sertipikadong legal na salin</p>
              <p className="mt-1 text-sm leading-relaxed text-emerald-900">
                Ang gabay na ito ay manu-manong isinulat upang ipaliwanag nang tumpak at malinaw ang mahahalagang tuntunin. Hindi pa ito sertipikado ng isang abogado bilang kumpletong legal na salin. Nasa ibaba ang buong English legal text; iyon, kasama ng tinanggap na order o kasunduan at umiiral na batas, ang masusunod kung may pagkakaiba sa pagpapakahulugan.
              </p>
            </div>
          )}

          {document.reviewPending && (
            <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="legal-review-pending">
              <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={20} />
              <div>
                <p className="font-semibold text-amber-900">{tagalogGuide ? "Hinihintay ang pagsusuri ng kwalipikadong abogado" : "Qualified legal review pending"}</p>
                <p className="mt-1 text-sm leading-relaxed text-amber-800">{tagalogGuide ? "Hindi pa naaaprubahan ng kwalipikadong abogado ang kasalukuyang legal text para gamiting panghuling batayan sa production. Patuloy na masusunod ang tinanggap na order at mga batas na hindi maaaring isantabi." : "This working legal text has not yet been approved by a qualified lawyer for final production reliance. The accepted order and mandatory law continue to apply."}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[15rem_minmax(0,1fr)] gap-8 items-start">
            <aside className="lg:sticky lg:top-28 rounded-2xl border border-[#E2E8F0] bg-white p-5 max-h-[70vh] overflow-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#10B981] mb-3">{tagalogGuide ? "Sa pahinang ito" : "On this page"}</p>
              <nav className="space-y-2" aria-label={`${tagalogGuide?.title || document.title} sections`}>
                {(tagalogGuide
                  ? tagalogGuide.sections.map((section, index) => ({ text: section.heading, id: slugify(section.heading, index), index }))
                  : sections.slice(0, 32)
                ).map((section) => (
                  <a key={`${section.id}-${section.index}`} href={`#${section.id}`} className="block text-sm leading-snug text-[#64748B] hover:text-[#059669]">{section.text}</a>
                ))}
              </nav>
            </aside>

            <article className="rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-9 md:p-12 shadow-sm">
              <div className="mb-8 rounded-xl border-l-4 border-[#10B981] bg-[#ECFDF5] p-5 flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 shrink-0 text-[#059669]" size={20} />
                <p className="text-[#1E293B] leading-relaxed">{tagalogGuide ? "Ang pananagutan ay nakabatay sa napiling plano at tinanggap na saklaw. May mga pananagutang saklaw ng MySiteCare, may nananatili sa kliyente, at may nakasalalay sa mga third-party provider." : "Responsibility depends on the selected plan and accepted scope. Some responsibilities belong to MySiteCare, some remain with the Client, and some depend on third-party providers."}</p>
              </div>

              {tagalogGuide ? (
                <>
                  {tagalogGuide.sections.map((section, index) => (
                    <section key={section.heading} id={slugify(section.heading, index)} className="scroll-mt-28">
                      <h2 className="mt-10 mb-3 text-xl sm:text-2xl font-bold font-display text-[#0F172A] first:mt-0">{section.heading}</h2>
                      <p className="mb-4 text-[#475569] leading-7">{section.body}</p>
                    </section>
                  ))}
                  <details className="mt-12 rounded-2xl border border-[#CBD5E1] bg-[#F8FAFC] p-5" data-no-translate data-testid="full-english-legal-text">
                    <summary className="cursor-pointer font-semibold text-[#0F172A]">Basahin ang kumpletong English legal text na siyang opisyal na batayan</summary>
                    <div className="mt-8 border-t border-[#E2E8F0] pt-2">
                      {document.blocks.map((text, index) => {
                        if (isHeading(text)) {
                          const level = headingLevel(text);
                          return level === 3
                            ? <h3 key={index} className="mt-8 mb-3 text-lg font-semibold font-display text-[#0F172A]">{text}</h3>
                            : <h2 key={index} className="mt-10 mb-4 text-2xl font-bold font-display text-[#0F172A]">{text}</h2>;
                        }
                        return <p key={index} className="mb-3 text-[#475569] leading-7"><InlineText text={text} /></p>;
                      })}
                    </div>
                  </details>
                </>
              ) : document.blocks.map((text, index) => {
                if (isHeading(text)) {
                  const level = headingLevel(text);
                  const id = slugify(text, index);
                  return level === 3
                    ? <h3 key={index} id={id} className="scroll-mt-28 mt-8 mb-3 text-lg font-semibold font-display text-[#0F172A]">{text}</h3>
                    : <h2 key={index} id={id} className="scroll-mt-28 mt-12 mb-4 text-2xl font-bold font-display text-[#0F172A] first:mt-0">{text}</h2>;
                }
                if (/^(Includes|Excludes|This may include|These may include|Where included):?$/i.test(text)) {
                  return <p key={index} className="mt-5 mb-2 font-semibold text-[#1E293B]">{text}</p>;
                }
                return <p key={index} className="mb-3 text-[#475569] leading-7"><InlineText text={text} /></p>;
              })}
            </article>
          </div>
        </Container>
      </section>
    </div>
  );
}
